import React, { useState } from "react";
import {
  Box,
  Container,
  Heading,
  Text,
  VStack,
  HStack,
  Button,
  Icon,
  Badge,
  SimpleGrid,
  Input,
  FormControl,
  FormLabel,
} from "@chakra-ui/react";
import {
  FaTimesCircle,
  FaEnvelope,
  FaCheckCircle,
  FaArrowRight,
  FaPhone,
  FaLightbulb,
  FaBolt,
  FaHome,
  FaLeaf,
} from "react-icons/fa";
import { Link as RouterLink } from "react-router-dom";
import { NavbarWithCallToAction } from "../../../components/chakra/NavbarWithCallToAction/NavbarWithCallToAction";
import { SolarFooter } from "../../../components/chakra/SolarFooter/SolarFooter";
import { SEOHead } from "../../../components/SEOHead";

const SOLAR_GOLD = "#C69214";
const SOLAR_GOLD_HOVER = "#a07e10";
const NAVY = "#141C49";

const SolarSavingsNoQualify: React.FC = () => {
  const [email, setEmail] = useState("");
  const [name, setName] = useState("");
  const [subscribed, setSubscribed] = useState(false);

  const handleSubscribe = () => {
    if (email) setSubscribed(true);
  };

  return (
    <Box minH="100vh" bg="white">
      <SEOHead
        pagePath="/solar/battery-rebates/no-qualify"
        fallbackTitle="Battery Rebate Eligibility | One Solar Australia"
        fallbackDescription="Current battery rebate programs are available to homeowners. We can still help renters lower their electricity bills — find out how."
      />
      <NavbarWithCallToAction />

      {/* ── Hero ── */}
      <Box bg={NAVY} color="white" py={20}>
        <Container maxW="container.xl">
          <VStack spacing={6} textAlign="center">
            <Icon as={FaTimesCircle} boxSize={16} color={SOLAR_GOLD} />
            <Heading size="2xl" fontWeight="bold">
              We're Sorry — You're Not Currently Eligible
            </Heading>
            <Text fontSize="xl" color="rgba(255,255,255,0.85)" maxW="2xl">
              Current battery rebate programs are only available to homeowners.
              As a renter, you don't qualify right now — but there's still plenty
              we can do to help lower your bills.
            </Text>
          </VStack>
        </Container>
      </Box>

      {/* ── Why Renters Don't Qualify + Immediate Help ── */}
      <Container maxW="container.xl" py={{ base: 16, md: 24 }}>
        <SimpleGrid columns={{ base: 1, md: 2 }} spacing={16} alignItems="start">

          {/* Left: explanation */}
          <VStack align="flex-start" spacing={6}>
            <Badge bg={SOLAR_GOLD} color="white" px={4} py={2} borderRadius="full" fontSize="sm" textTransform="uppercase" letterSpacing="wider">
              Why You Don't Qualify
            </Badge>
            <Heading size="xl" color={NAVY} lineHeight="1.2">
              Battery Rebates Require Home Ownership
            </Heading>
            <Text color="gray.600" fontSize="lg" lineHeight="1.8">
              Government rebate programs for solar batteries require the applicant to own
              the property. As a renter, your landlord would need to apply — but that
              doesn't mean you're out of options.
            </Text>
            <Text color="gray.600" fontSize="md" lineHeight="1.8">
              Energy costs are rising for everyone. We can help you reduce your bills
              in other ways, notify you when new renter-friendly programs become available,
              or even help you start the conversation with your landlord.
            </Text>
            <Box
              w="full"
              p={6}
              bg="gray.50"
              borderRadius="2xl"
              border="1px solid"
              borderColor={`${SOLAR_GOLD}40`}
            >
              <Text fontWeight="700" color={NAVY} mb={3} fontSize="sm" textTransform="uppercase" letterSpacing="wider">
                Your options right now
              </Text>
              <VStack align="flex-start" spacing={3}>
                {[
                  "Stay informed — we'll notify you when renter rebates launch",
                  "Talk to your landlord about solar or battery installation",
                  "Explore energy-saving tips to cut your bills immediately",
                  "Call us — we may still be able to help in your situation",
                ].map((item) => (
                  <HStack key={item} spacing={3} align="flex-start">
                    <Icon as={FaCheckCircle} color={SOLAR_GOLD} boxSize={4} mt="3px" flexShrink={0} />
                    <Text fontSize="sm" color="gray.700" lineHeight="1.7">{item}</Text>
                  </HStack>
                ))}
              </VStack>
            </Box>
          </VStack>

          {/* Right: stay informed form */}
          <Box
            bg="white"
            borderRadius="2xl"
            p={{ base: 8, md: 10 }}
            boxShadow="xl"
            border="2px solid"
            borderColor={`${SOLAR_GOLD}30`}
          >
            {subscribed ? (
              <VStack spacing={5} textAlign="center" py={8}>
                <Icon as={FaCheckCircle} boxSize={16} color={SOLAR_GOLD} />
                <Heading size="lg" color={NAVY}>You're on the list!</Heading>
                <Text color="gray.600" fontSize="md">
                  We'll be in touch as soon as new programs launch in your area
                  or when eligibility rules change.
                </Text>
                <Button
                  as={RouterLink}
                  to="/solar"
                  size="lg"
                  bg={SOLAR_GOLD}
                  color="white"
                  _hover={{ bg: SOLAR_GOLD_HOVER }}
                  rightIcon={<FaArrowRight />}
                >
                  Explore Solar Options
                </Button>
              </VStack>
            ) : (
              <VStack spacing={6} align="stretch">
                <VStack spacing={2} align="flex-start">
                  <Badge bg={SOLAR_GOLD} color="white" px={3} py={1} borderRadius="full" fontSize="xs" textTransform="uppercase" letterSpacing="wider">
                    Stay Informed
                  </Badge>
                  <Heading size="lg" color={NAVY}>
                    Be First to Know When You Qualify
                  </Heading>
                  <Text color="gray.600" fontSize="sm" lineHeight="1.7">
                    Rebate programs change. Subscribe and we'll notify you the moment
                    renter-eligible options become available in your area.
                  </Text>
                </VStack>

                <FormControl>
                  <FormLabel fontWeight="600" color={NAVY} fontSize="sm">Your Name</FormLabel>
                  <Input
                    value={name}
                    onChange={(e) => setName(e.target.value)}
                    placeholder="Jane Smith"
                    size="lg"
                    bg="gray.50"
                    borderColor="gray.200"
                    _focus={{ borderColor: SOLAR_GOLD, boxShadow: `0 0 0 1px ${SOLAR_GOLD}` }}
                  />
                </FormControl>

                <FormControl isRequired>
                  <FormLabel fontWeight="600" color={NAVY} fontSize="sm">Email Address</FormLabel>
                  <Input
                    type="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    placeholder="you@example.com"
                    size="lg"
                    bg="gray.50"
                    borderColor="gray.200"
                    _focus={{ borderColor: SOLAR_GOLD, boxShadow: `0 0 0 1px ${SOLAR_GOLD}` }}
                  />
                </FormControl>

                <Button
                  bg={SOLAR_GOLD}
                  color="white"
                  size="lg"
                  _hover={{ bg: SOLAR_GOLD_HOVER }}
                  onClick={handleSubscribe}
                  isDisabled={!email}
                  rightIcon={<FaEnvelope />}
                >
                  Notify Me When I Qualify
                </Button>

                <HStack justify="center" spacing={2} pt={1}>
                  <Icon as={FaPhone} color={NAVY} boxSize={3} />
                  <Text fontSize="sm" color="gray.500">
                    Prefer to talk?{" "}
                    <Text
                      as="a"
                      href="tel:1300663765"
                      fontWeight="700"
                      color={NAVY}
                      _hover={{ color: SOLAR_GOLD }}
                    >
                      Call 1300 663 765
                    </Text>
                  </Text>
                </HStack>
              </VStack>
            )}
          </Box>
        </SimpleGrid>
      </Container>

      {/* ── Want to Lower Your Bills Now? ── */}
      <Box bg="gray.50" py={{ base: 16, md: 20 }}>
        <Container maxW="container.xl">
          <VStack spacing={4} mb={12} textAlign="center">
            <Badge bg={SOLAR_GOLD} color="white" px={4} py={2} borderRadius="full" fontSize="sm" textTransform="uppercase" letterSpacing="wider">
              Reduce Bills Now
            </Badge>
            <Heading size="xl" color={NAVY}>
              Want to Lower Your Bills Right Now?
            </Heading>
            <Text fontSize="lg" color="gray.600" maxW="2xl">
              You don't need to own your home to start saving on energy. Here are
              some practical steps you can take today.
            </Text>
          </VStack>

          <SimpleGrid columns={{ base: 1, md: 2, lg: 4 }} spacing={6}>
            {[
              {
                icon: FaLightbulb,
                title: "Switch to LED",
                body: "Replacing halogen and fluorescent bulbs with LED lighting can cut your lighting costs by up to 75% with zero landlord permission needed.",
              },
              {
                icon: FaBolt,
                title: "Compare Energy Plans",
                body: "Many renters are on default plans. Switching retailers or plans costs nothing and can save hundreds of dollars a year on the same usage.",
              },
              {
                icon: FaHome,
                title: "Talk to Your Landlord",
                body: "Solar and battery installations increase property value. Many landlords are open to the conversation — we can help you frame it.",
              },
              {
                icon: FaLeaf,
                title: "Smart Appliance Use",
                body: "Running dishwashers, washing machines, and dryers during off-peak hours (before 3pm) can significantly reduce your electricity spend.",
              },
            ].map((tip) => (
              <Box
                key={tip.title}
                bg="white"
                borderRadius="2xl"
                p={7}
                boxShadow="md"
                border="1px solid"
                borderColor="gray.100"
                _hover={{ boxShadow: "xl", borderColor: SOLAR_GOLD }}
                transition="all 0.3s"
              >
                <Icon as={tip.icon} boxSize={9} color={SOLAR_GOLD} mb={4} />
                <Heading size="sm" color={NAVY} mb={3}>{tip.title}</Heading>
                <Text color="gray.600" fontSize="sm" lineHeight="1.8">{tip.body}</Text>
              </Box>
            ))}
          </SimpleGrid>
        </Container>
      </Box>

      {/* ── Talk to a Landlord CTA ── */}
      <Box bg={NAVY} py={{ base: 16, md: 20 }}>
        <Container maxW="container.lg">
          <VStack spacing={6} textAlign="center" color="white">
            <Badge bg={SOLAR_GOLD} color="white" px={4} py={2} borderRadius="full" fontSize="sm" textTransform="uppercase" letterSpacing="wider">
              Not a Permanent Block
            </Badge>
            <Heading size="xl" fontWeight="bold">
              We May Still Be Able to Help
            </Heading>
            <Text fontSize="lg" color="rgba(255,255,255,0.85)" maxW="2xl" lineHeight="1.8">
              Every situation is different. Call us and speak with one of our solar advisors —
              there may be options specific to your rental arrangement that we can explore together.
            </Text>
            <HStack spacing={4} pt={2} flexWrap="wrap" justify="center">
              <Button
                as="a"
                href="tel:1300663765"
                size="lg"
                bg={SOLAR_GOLD}
                color="white"
                _hover={{ bg: SOLAR_GOLD_HOVER }}
                leftIcon={<Icon as={FaPhone} />}
                px={10}
              >
                Call 1300 663 765
              </Button>
              <Button
                as={RouterLink}
                to="/solar/battery-rebates"
                size="lg"
                variant="outline"
                borderColor="white"
                color="white"
                _hover={{ bg: SOLAR_GOLD, borderColor: SOLAR_GOLD }}
                px={8}
              >
                Back to Rebates Page
              </Button>
            </HStack>
          </VStack>
        </Container>
      </Box>

      <SolarFooter />
    </Box>
  );
};

export default SolarSavingsNoQualify;
